import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { renderCciQuotePages } from './cciQuotePdfGenerator';
import { renderVduraQuotePages, buildVduraQuoteFilename } from './quotePdfGenerator';
import { stampPageNumbers, VDURA_FOOTER } from './stampPageNumbers';
import { uploadToExternalS3 } from './externalS3Upload';

// jspdf-autotable 5 no longer attaches doc.autoTable on a bare import (it only
// self-applies to a global window.jsPDF, which the ESM import never sets), so
// the plugin is applied explicitly. CONF-237.
applyPlugin(jsPDF);

/**
 * CONF-186: one PDF for a CCI quote — a lead quotation summary, then the CCI
 * hardware quote, then the VDURA software quote.
 *
 * The lead section is the standard VDURA quote page over the full priced BOM,
 * retitled and with its Terms and Conditions swapped for an Ordering
 * Instructions pointer to the two executable quotes behind it. It is "the page
 * you read"; the two sections after it are "the documents you execute".
 *
 * `showHwReference` reads backwards between the two sections and is worth
 * spelling out: it is the switch that *suppresses* the priced hardware table.
 * So `false` on the lead page is what turns the itemised hardware pricing ON —
 * the whole point of the lead section — while `true` on the back software quote
 * keeps its price-free Hardware Reference table, which is what defines that
 * quote's scope without pricing hardware VDURA does not sell.
 *
 * The document is A4 throughout. The CCI section is authored against US Letter
 * and rides along with a tighter right margin (9mm rather than 15mm); it is
 * the safe direction, since normalising to Letter would shorten every page by
 * 18mm and risk the VDURA layout's bottom-of-page content.
 *
 * Every render is awaited in turn. They each await image compression
 * internally, and sharing one document means an un-awaited render would
 * interleave its pages with the next section's.
 */
export async function generateCombinedCciQuotePDF({ metrics, bom, swRows, cciRows, userName, config, pricingData }) {
  const doc = new jsPDF({
    compress: true,
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    encryption: {
      userPassword: null,
      ownerPassword: null,
      // annot-forms keeps the CCI section's quote-number / customer-ID fields
      // fillable inside the combined document, not just the standalone one.
      userPermissions: ['print', 'copy', 'annot-forms'],
    },
  });

  // Lead: the standard quote page, retitled, terms swapped for the pointer.
  await renderVduraQuotePages(doc, metrics, bom, userName, config, pricingData, {
    isFullQuote: true,
    showHwReference: false,
    termsMode: 'pointer',
    titles: { title: 'Quotation Summary', bandLabel: 'Complete System Summary' },
    suppressEmptyNotes: true,
  });

  doc.addPage();
  await renderCciQuotePages(doc, cciRows, config, userName);

  doc.addPage();
  // Back: the executable software quote, as today but retitled.
  await renderVduraQuotePages(doc, metrics, swRows, userName, config, pricingData, {
    isFullQuote: true,
    showHwReference: true,
    titles: { title: 'Software & Support Quotation' },
  });

  // One stamp pass over the finished document, so "Page 3 of 11" counts every
  // section rather than restarting per generator.
  stampPageNumbers(doc, VDURA_FOOTER);

  const filename = buildVduraQuoteFilename(config, true);
  doc.save(filename);

  try {
    await uploadToExternalS3(filename, doc.output('blob'), 'application/pdf');
  } catch (err) {
    console.error('External S3 PDF upload failed:', err);
  }

  return filename;
}

export default generateCombinedCciQuotePDF;
